import { useEffect, useRef, useState } from "react";
import { getVPH } from "@/lib/viewport";

// Standalone scroll-driven frame-sequence canvas for the Story Builder engine.
// Intentionally separate from the locked homepage FrameSequenceCanvas so the
// production experience is never affected.
//   preset="story": whole frame, contained, over a blurred backdrop.
//   preset="cover": full-bleed cover.
export const StoryFrameCanvas = ({
  framesBaseUrl,
  prefix = "frame-",
  pad = 3,
  total = 0,
  sectionSelector,
  preset = "story",
  onProgress,
}) => {
  const canvasRef = useRef(null);
  const imagesRef = useRef([]);
  const frameRef = useRef(0);
  const rafRef = useRef(null);
  const [loaded, setLoaded] = useState(0);
  const [isReady, setIsReady] = useState(false);

  const framePath = (i) =>
    `${framesBaseUrl}/${prefix}${String(i).padStart(pad, "0")}.jpg`;

  useEffect(() => {
    if (!framesBaseUrl || !total) return;
    let mounted = true;
    let count = 0;
    const imgs = new Array(total);
    for (let i = 1; i <= total; i++) {
      const img = new Image();
      img.src = framePath(i);
      img.onload = img.onerror = () => {
        if (!mounted) return;
        count += 1;
        setLoaded(count);
        if (count === total) setIsReady(true);
      };
      imgs[i - 1] = img;
    }
    imagesRef.current = imgs;
    return () => {
      mounted = false;
    };
  }, [framesBaseUrl, total]);

  const draw = (index) => {
    const canvas = canvasRef.current;
    const img = imagesRef.current[index];
    if (!canvas || !img || !img.width) return;
    const ctx = canvas.getContext("2d");
    const cw = canvas.width;
    const ch = canvas.height;
    const iw = img.width;
    const ih = img.height;
    ctx.clearRect(0, 0, cw, ch);

    // blurred backdrop for letterboxing
    const coverScale = Math.max(cw / iw, ch / ih) * 1.18;
    ctx.save();
    ctx.filter = "blur(28px) brightness(0.5) saturate(1.1)";
    ctx.drawImage(
      img,
      (cw - iw * coverScale) / 2,
      (ch - ih * coverScale) / 2,
      iw * coverScale,
      ih * coverScale
    );
    ctx.restore();

    if (preset === "cover") {
      const s = Math.max(cw / iw, ch / ih);
      ctx.drawImage(img, (cw - iw * s) / 2, (ch - ih * s) / 2, iw * s, ih * s);
      return;
    }
    const s = Math.min(cw / iw, ch / ih) * 0.94;
    ctx.drawImage(img, (cw - iw * s) / 2, (ch - ih * s) / 2, iw * s, ih * s);
  };

  useEffect(() => {
    let lastW = -1;
    const resize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      if (window.innerWidth === lastW) return;
      lastW = window.innerWidth;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const vh = getVPH();
      canvas.width = window.innerWidth * dpr;
      canvas.height = vh * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = vh + "px";
      draw(frameRef.current);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [isReady]);

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const section = document.querySelector(sectionSelector);
        if (!section) return;
        const rect = section.getBoundingClientRect();
        const totalScroll = section.offsetHeight - getVPH();
        const scrolled = Math.min(Math.max(-rect.top, 0), totalScroll);
        const progress = totalScroll > 0 ? scrolled / totalScroll : 0;
        const target = Math.min(total - 1, Math.round(progress * (total - 1)));
        if (target !== frameRef.current) {
          frameRef.current = target;
          draw(target);
        }
        onProgress && onProgress(progress);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [isReady, onProgress, sectionSelector, total]);

  const pct = total ? Math.round((loaded / total) * 100) : 0;

  return (
    <>
      <canvas
        ref={canvasRef}
        data-testid="story-frame-canvas"
        className="block h-full w-full"
      />
      {!isReady && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-[#070b18]">
          <p className="font-display text-3xl text-amber-300">✦</p>
          <div className="mt-6 h-[2px] w-52 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-amber-200 transition-all duration-200"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-3 text-xs tracking-[0.3em] text-amber-100/40">{pct}%</p>
        </div>
      )}
    </>
  );
};
